import moment from 'moment';
import _ from 'underscore';

import {naturalSort, omitNull} from 'common/utils';
import ServiceException from 'common/types/ServiceException';
import ServiceAnnouncement from 'common/types/ServiceAnnouncement';
import ServiceAddress from 'common/types/ServiceAddress';
import ServiceRegion from 'common/types/ServiceRegion';


export const serviceColors = [
  {value: '#e53935', label: 'Red'},
  {value: '#fa3176', label: 'Pink'},
  {value: '#ff5b00', label: 'Orange'},
  {value: '#43a047', label: 'Green'},
  {value: '#009688', label: 'Aqua'},
  {value: '#1e88e5', label: 'Blue'},
  {value: '#3c31bd', label: 'Dark Blue'},
  {value: '#9c27b0', label: 'Purple'},
  {value: '#a24f31', label: 'Brown'},
];

export const stopRestrictions = {
  UNRESTRICTED: 'unrestricted',
  RESTRICT_BOTH: 'restrict_both',
  RESTRICT_ONE: 'restrict_one',
};

const readableStopRestrictions = {
  [stopRestrictions.UNRESTRICTED]: 'No Restrictions',
  [stopRestrictions.RESTRICT_BOTH]: 'Restrict pickup AND drop-off',
  [stopRestrictions.RESTRICT_ONE]: 'Restrict pickup OR drop-off',
};

export const serviceTypes = {
  ONDEMAND: 'on_demand',
  INADVANCE: 'in_advance',
};

const SECONDS_PER_DAY = 86400;
const DEFAULT_DRIVER_WAIT_SECONDS = 300;

export default class Service {
  /* eslint-disable camelcase */
  constructor({
    service_id,
    service_type,
    archived_at,
    name,
    color,
    start_date,
    end_date,
    start_time,
    end_time,
    in_service,
    sunday,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    on_demand_enabled,
    in_advance_enabled,
    recurring_rides_enabled,
    fare_required,
    fare_price,
    token_transit_use_deeplink,
    limit_capacity_per_trip,
    max_capacity_per_trip,
    managed_mode,
    max_schedule_time,
    min_time_in_advance,
    min_time_in_advance_enabled,
    ride_request_allowed,
    rider_restricted,
    shibboleth_restricted,
    shibboleth_affiliation,
    stop_restriction,
    wheelchair_accessible,
    accommodate_bicycle,
    exceptions,
    announcements,
    vehicles,
    addresses,
    regions,
    token_transit_fare_id,
    group_permissions,
    driver_wait_enabled,
    driver_wait_seconds,
  }) {
    this.service_id = service_id || null;
    this.service_type =
      service_type || serviceTypes.ONDEMAND;
    this.archived_at = archived_at || null;
    this.name = name || '';
    this.color = color || '1e88e5';  // default to blue
    this.start_date = start_date || '';
    this.end_date = end_date || '';
    this.start_time = start_time === undefined ? null : start_time;
    this.end_time = end_time === undefined ? null : end_time;
    this.in_service = Boolean(in_service);
    this.sunday = Boolean(sunday);
    this.monday = Boolean(monday);
    this.tuesday = Boolean(tuesday);
    this.wednesday = Boolean(wednesday);
    this.thursday = Boolean(thursday);
    this.friday = Boolean(friday);
    this.saturday = Boolean(saturday);
    this.on_demand_enabled = on_demand_enabled === undefined ?
      true : Boolean(on_demand_enabled);
    this.in_advance_enabled = in_advance_enabled === undefined ?
      false : Boolean(in_advance_enabled);
    this.recurring_rides_enabled = recurring_rides_enabled ===
      undefined ? false : Boolean(recurring_rides_enabled);
    this.fare_required = Boolean(fare_required);
    this.fare_price = fare_price || null;
    this.token_transit_use_deeplink = Boolean(token_transit_use_deeplink);
    this.limit_capacity_per_trip = Boolean(limit_capacity_per_trip) || false;
    this.max_capacity_per_trip = max_capacity_per_trip === undefined ? 1 : max_capacity_per_trip;
    this.managed_mode = Boolean(managed_mode);
    this.max_schedule_time = max_schedule_time || null;
    this.min_time_in_advance = min_time_in_advance || null;
    this.min_time_in_advance_enabled = Boolean(min_time_in_advance_enabled) || false;
    this.ride_request_allowed = Boolean(ride_request_allowed);
    this.rider_restricted = Boolean(rider_restricted);
    this.shibboleth_restricted = Boolean(shibboleth_restricted);
    this.shibboleth_affiliation = shibboleth_affiliation || '';
    this.stop_restriction =
      stop_restriction || stopRestrictions.UNRESTRICTED;
    this.wheelchair_accessible = Boolean(wheelchair_accessible);
    this.accommodate_bicycle = Boolean(accommodate_bicycle);
    this.exceptions = _.map(exceptions, (exception) => new ServiceException(exception));
    this.announcements = _.map(announcements,
      (announcement) => new ServiceAnnouncement(announcement));
    this.vehicles = vehicles || [];
    this.addresses = addresses && addresses.length
      ? addresses.map((address) => new ServiceAddress(address)) : [];
    this.regions = regions && regions.length
      ? regions.map((region) => new ServiceRegion(region)) : [];
    this.token_transit_fare_id = token_transit_fare_id || '';
    this.group_permissions = group_permissions || [];
    this.driver_wait_enabled = Boolean(driver_wait_enabled) || false;
    this.driver_wait_seconds = typeof driver_wait_seconds === 'number' ?
      driver_wait_seconds : DEFAULT_DRIVER_WAIT_SECONDS;
  }
  /* eslint-enable camelcase */

  get id() {
    return this.service_id;
  }

  get daysOfWeek() {
    const days = [
      {label: 'Sun', value: this.sunday},
      {label: 'Mon', value: this.monday},
      {label: 'Tue', value: this.tuesday},
      {label: 'Wed', value: this.wednesday},
      {label: 'Thu', value: this.thursday},
      {label: 'Fri', value: this.friday},
      {label: 'Sat', value: this.saturday},
    ];
    return _.map(_.filter(days, (day) => day.value), (day) => day.label).join(', ');
  }

  get daysOfWeekArray() {
    return [
      this.sunday,
      this.monday,
      this.tuesday,
      this.wednesday,
      this.thursday,
      this.friday,
      this.saturday,
    ];
  }

  get stopRestrictions() {
    return readableStopRestrictions[this.stop_restriction];
  }

  get startDate() {
    if (this.start_date) return moment(this.start_date);
    return moment();
  }

  set startDate(value) {
    const targetDate = moment(value, 'MM/DD/YYYY').startOf('day');
    this.start_date = targetDate.format('YYYY-MM-DD');
  }

  get endDate() {
    if (this.end_date) return moment(this.end_date);
    return moment();
  }

  set endDate(value) {
    const targetDate = moment(value, 'MM/DD/YYYY').startOf('day');
    this.end_date = targetDate.format('YYYY-MM-DD');
  }

  get formattedStartDate() {
    return this.startDate.format('MMMM D, YYYY');
  }

  get formattedEndDate() {
    return this.endDate.format('MMMM D, YYYY');
  }

  get schedulingEnabled() {
    return this.service_type === serviceTypes.INADVANCE || this.in_advance_enabled;
  }

  get currentException() {
    return this.exceptions.find((ex) => moment.utc().isBetween(ex.startTime, ex.endTime));
  }

  get days() {
    return {
      sunday: this.sunday,
      monday: this.monday,
      tuesday: this.tuesday,
      wednesday: this.wednesday,
      thursday: this.thursday,
      friday: this.friday,
      saturday: this.saturday,
    };
  }

  set days(dayMap) {
    this.sunday = dayMap.sunday;
    this.monday = dayMap.monday;
    this.tuesday = dayMap.tuesday;
    this.wednesday = dayMap.wednesday;
    this.thursday = dayMap.thursday;
    this.friday = dayMap.friday;
    this.saturday = dayMap.saturday;
  }

  containsDate(day) {
    const date = moment(day);
    if (!date.isBetween(moment(this.startDate), moment(this.endDate), 'day', '[]')) {
      return false;
    }
    return this[date.format('dddd').toLowerCase()];
  }

  // dt needs to be a localized time for the Agency because of how start_time and
  // end_time are gtfs time offsets for the Agency's local timezone.
  inServiceAt(dt) {
    const exception = _.find(this.exceptions, (ex) =>
      moment.utc(dt).isBetween(ex.startTime, ex.endTime, null, '[]')
    );
    if (exception !== undefined) {
      return exception.type === 'service_added';
    }
    return _.any(this.activeServiceStartDates(dt), (startDate) => {
      const secondsToday = dt.hours() * 3600 + dt.minutes() * 60 + dt.seconds();
      const offsetSeconds = dt.diff(startDate, 'days') * SECONDS_PER_DAY;
      return (this.start_time - offsetSeconds <= secondsToday &&
              secondsToday <= this.end_time - offsetSeconds);
    });
  }

  inServiceOn(day, tz) {
    const dayStart = moment(day).tz(tz).startOf('day');
    const dayEnd = dayStart.clone().endOf('day');
    const exceptions = _.filter(this.exceptions, (ex) =>
      dayStart.isBetween(ex.startTime.clone().tz(tz), ex.endTime.clone().tz(tz), 'day', '[]')
    );
    if (exceptions.length) {
      // Look at all service_removed exceptions for the day and see if there was
      // active service at the start or end of any of those exceptions
      // (between start and end of day)
      const removedExceptions = _.where(exceptions, {type: 'service_removed'});
      if (removedExceptions.length) {
        return _.any(removedExceptions, (ex) => {
          const startTime = ex.startTime.clone().tz(tz).subtract(1, 'seconds');
          const endTime = ex.endTime.clone().tz(tz).add(1, 'seconds');
          const start = this.inServiceAt(startTime.isBefore(dayStart) ? dayStart : startTime);
          const end = this.inServiceAt(endTime.isAfter(dayEnd) ? dayEnd : endTime);
          return start || end;
        });
      }

      // if service has been added at all today, then return true
      const added = _.where(exceptions, {type: 'service_added'}).length > 0;
      if (added) {
        return true;
      }
    }

    // there were no exceptions so just look at regular service dates
    return this.activeServiceStartDates(day).length > 0;
  }

  // Given a day, return the start dates of all services that run at some point during that day.
  activeServiceStartDates(day) {
    const startDays = [];
    const minOffset = Math.floor(this.start_time / SECONDS_PER_DAY);
    const maxOffset = Math.ceil(this.end_time / SECONDS_PER_DAY) - 1;
    for (let offset = minOffset; offset <= maxOffset; offset++) {
      const startDay = moment(day).subtract(offset, 'days');
      if (this.containsDate(startDay)) {
        startDays.push(startDay);
      }
    }
    return startDays;
  }

  clone() {
    return new Service(this.serialize());
  }

  compare(other) {
    return naturalSort.compare(this.name, other.name);
  }

  serialize() {
    return omitNull({
      service_id: this.service_id,
      service_type: this.service_type,
      archived_at: this.archived_at,
      name: this.name || null,
      color: this.color || null,
      start_date: this.start_date || moment().format('YYYY-MM-DD'),
      end_date: this.end_date || moment().format('YYYY-MM-DD'),
      start_time: this.start_time,
      end_time: this.end_time,
      in_service: this.in_service,
      sunday: this.sunday,
      monday: this.monday,
      tuesday: this.tuesday,
      wednesday: this.wednesday,
      thursday: this.thursday,
      friday: this.friday,
      saturday: this.saturday,
      on_demand_enabled: this.on_demand_enabled,
      in_advance_enabled: this.in_advance_enabled,
      recurring_rides_enabled: this.recurring_rides_enabled,
      fare_required: this.fare_required,
      fare_price: this.fare_price,
      token_transit_use_deeplink: this.token_transit_use_deeplink,
      limit_capacity_per_trip: this.limit_capacity_per_trip,
      max_capacity_per_trip: this.max_capacity_per_trip,
      managed_mode: this.managed_mode,
      max_schedule_time: this.max_schedule_time,
      min_time_in_advance: this.min_time_in_advance,
      min_time_in_advance_enabled: this.min_time_in_advance_enabled,
      rider_restricted: this.rider_restricted,
      shibboleth_restricted: this.shibboleth_restricted,
      shibboleth_affiliation: this.shibboleth_affiliation,
      stop_restriction: this.stop_restriction,
      wheelchair_accessible: this.wheelchair_accessible,
      accommodate_bicycle: this.accommodate_bicycle,
      exceptions: _.map(this.exceptions, (exception) => exception.serialize()),
      announcements: _.map(this.announcements, (announcement) => announcement.serialize()),
      vehicles: this.vehicles.slice(),
      addresses: _.map(this.addresses, (address) => address.serialize()),
      regions: _.map(this.regions, (region) => region.serialize()),
      token_transit_fare_id: this.token_transit_fare_id,
      group_permissions: this.group_permissions,
      driver_wait_enabled: this.driver_wait_enabled,
      driver_wait_seconds: this.driver_wait_seconds,
    });
  }

  isDifferent(other) {
    let exisitngItem;
    let otherItem;
    /* eslint-disable no-unused-vars */
    /* eslint-disable no-param-reassign */
    if (other && other.serialize) {
      exisitngItem = this.serialize();
      otherItem = other.serialize();

      if (exisitngItem.addresses && exisitngItem.addresses.length) {
        exisitngItem = exisitngItem.addresses.map((addr) => {
          addr = exisitngItem.addresses.sort((a, b) => a.address_id - b.address_id);
          return exisitngItem;
        });
      }

      if (otherItem.addresses && otherItem.addresses.length) {
        otherItem = otherItem.addresses.map((addr) => {
          addr = otherItem.addresses.sort((a, b) => a.address_id - b.address_id);
          return otherItem;
        });
      }
    }
    /* eslint-enable no-unused-vars */
    /* eslint-enable no-param-reassign */

    return other && other.serialize ? !_.isEqual(exisitngItem, otherItem) : true;
  }
}



// WEBPACK FOOTER //
// ./src/common/types/Service.js